import { useEffect } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { useErrorToast } from '../../../hooks/toasts/error-toast.hook';

import { useZoneServiceLogMutation, useZoneServiceLogImagesMutation } from 'store/apis';
import { IZone } from '../../../models';

interface UseZoneServiceLogFormManagerProps {
  zone: IZone;
  employeeCode: string;
  next?: (data?: any) => void;
}

export interface ZoneServiceLogFormValues {
  serviceItems: { id: number; serviced: boolean }[];
  resets_countdown: boolean;
  employeeCode: string;
  files: File[];
}

export const useZoneServiceLogFormManager = ({
  zone,
  employeeCode,
  next = () => {},
}: UseZoneServiceLogFormManagerProps) => {
  const errorToast = useErrorToast();
  const [mutate, zoneRequest] = useZoneServiceLogMutation();
  const [postImages, { isLoading, isSuccess, isError, ...rest }] = useZoneServiceLogImagesMutation();

  const initialServiceItems = zone.service_items.map((s) => ({ id: s.id, serviced: false }));

  const initialValues: ZoneServiceLogFormValues = {
    serviceItems: initialServiceItems,
    files: [],
    resets_countdown: false,
    employeeCode,
  };

  const validationSchema = Yup.object({
    serviceItems: Yup.array(),
    resets_countdown: Yup.boolean(),
    employeeCode: Yup.string(),
    files: Yup.array().max(3, 'You can only upload 3 files').nullable().default([]),
  });

  const formManager = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (results) => {
      mutate({ zoneId: zone.id, body: results });
    },
  });

  useEffect(() => {
    if (!zoneRequest.isLoading && zoneRequest.isSuccess && !zoneRequest.isError) {
      // the log has been saved, now save the imaged
      console.log('log saved, save images');
      if (formManager.values.files.length > 0) {
        console.log('saving ', formManager.values.files.length, ' images');
        const form = new FormData();
        form.append('employeeCode', employeeCode);
        formManager.values.files.forEach((file) => {
          form.append('images[]', file);
        });
        postImages({ logId: zoneRequest.data.id as number, body: form });
      } else {
        const { data } = zoneRequest;

        next(data);
      }
    }

    if (zoneRequest.isError) {
      console.error('error saving log', zoneRequest.error);
      errorToast.open({
        message: (zoneRequest.error as any).data?.payload?.message || 'An unexpected error occurred',
      });
    }
  }, [zoneRequest.isLoading, zoneRequest.isSuccess, zoneRequest.isError]);

  useEffect(() => {
    if (!isLoading && isSuccess && !isError) {
      console.log('images saved');

      const { data } = zoneRequest;

      next(data);
    }

    if (isError) {
      console.log('error saving images', rest.error);
      errorToast.open({ message: rest.error as string });
    }
  }, [isLoading, isSuccess, isError]);

  return [
    {
      formManager,
      loading: isLoading,
    },
  ] as [
    {
      formManager: FormikProps<typeof initialValues>;
      loading: boolean;
    },
  ];
};
